/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import Snackbar, { SnackbarCloseReason } from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../core/auth';
import { initialize as initializeI18N } from '../core/i18n';
import { useProfileContext } from '../core/profile/ProfileProvider';
import { LocalStorageKey, InfoMsg } from '../core/types';
import { useFetch } from '../core/api';
import { mapLiquidAssetsGraph } from '../helpers';
import config from '../core/context/config/config';

interface Props {
  children: React.ReactNode;
  placeholder?: React.ReactNode;
}

const mapApiStructureToProfile = (data: any) => {
  const { leadInput, leadResult } = data;
  const { party, inventory, scenario, contactDetails } = leadInput;

  return {
    who: {
      ...party,
      email: contactDetails.email,
      mobile: contactDetails.mobile,
    },
    what: inventory,
    goals: {
      ...scenario,
      monthlySavingsValue: inventory.cashFlow?.monthlySavingsValue,
    },
    contact: contactDetails,
    result: {
      ...leadResult,
      liquidAssetsGraph: mapLiquidAssetsGraph(leadResult.liquidAssetsGraph),
    },
  };
};

const AppInitializer: React.FC<Props> = ({ children, placeholder }: Props) => {
  const [initialized, setInitialized] = useState(false);
  const [infoMsg, setInfoMsg] = useState<InfoMsg>({
    show: false,
    type: 'error',
    msg:
      // TODO: there is an issue with the useTranslation hook in this component, it's because the i18n is still being initialized, so we can't use the hook until this is complete
      'Toegang tot uw gegevens via deze mail is helaas verlopen. U kunt een nieuwe mail aanvragen om opnieuw toegang te verkrijgen.',
  });

  const auth = useAuth();
  const fetch = useFetch();
  const { setExistingProfile } = useProfileContext();

  const fetchProfile = () => {
    return new Promise<void>((resolve, reject) => {
      const params = new URLSearchParams(window.location.search);
      const accessToken = params.get('Authorization');
      const broker_uid = params.get('broker_uid');

      if (accessToken) {
        auth.setUserToken({
          accessToken,
        });

        fetch
          .get(`leads?broker_uid=${broker_uid}`)
          .then(data => {
            const {
              leadInput: {
                contactDetails: { email },
              },
            } = data;

            console.log('DATA', data);
            // we set this to know when to use a PUT or a POST request when requesting results
            localStorage.setItem('savedEmail', email);
            const profile = mapApiStructureToProfile(data);
            // whenever a value is changed, isDirty will be true and we will use a PUT request instead of a POST
            profile.result.isDirty = false;
            setExistingProfile(profile);
            resolve();
          })
          .catch(err => {
            console.log('error', err);
            setInfoMsg(state => ({
              ...state,
              show: true,
            }));
          });
      } else {
        resolve();
      }
    });
  };

  const handleClose = (reason?: SnackbarCloseReason) => {
    window.location.href =
      window.location.href.indexOf('localhost') > -1 ? '/' : config.baseName;
  };

  useEffect(() => {
    Promise.all([auth.initialize(), initializeI18N(), fetchProfile()]).then(
      () => {
        setInitialized(true);
      },
    );
  }, [auth]);

  return (
    <>
      <Snackbar
        open={infoMsg.show}
        autoHideDuration={5000}
        onClose={(event, reason) => handleClose(reason)}>
        <MuiAlert
          elevation={6}
          onClose={() => handleClose()}
          variant='filled'
          severity={infoMsg.type}>
          {infoMsg.msg}
        </MuiAlert>
      </Snackbar>
      {initialized ? children : placeholder ?? null}
    </>
  );
};

export default AppInitializer;
